// hooks/engagement/useEngagement.js

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { qbCustomersApi, engagementsApi, syncAllEngagementActuals } from '../../hooks/engagement/engagementApi';

// ── Engagements list ──────────────────────────────────────────────────
export const useEngagements = () => {
  return useQuery({
    queryKey: ['engagements'],
    queryFn: async () => {
      const response = await engagementsApi.listEngagements({ limit: 500 });
      return response?.data || response;
    },
    staleTime: 0,
    gcTime: 30 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchOnMount: true,
  });
};

// ── Single engagement ─────────────────────────────────────────────────
export const useEngagement = (engagementId, enabled = true) => {
  return useQuery({
    queryKey: ['engagement', engagementId],
    queryFn: () => engagementsApi.getEngagement(engagementId),
    enabled: enabled && !!engagementId,
    staleTime: 5 * 60 * 1000,
  });
};

// ── Create ────────────────────────────────────────────────────────────
export const useCreateEngagement = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload) => engagementsApi.createEngagement(payload),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ['engagements'], refetchType: 'active' });
      queryClient.invalidateQueries({ queryKey: ['engagements', 'won'] });
      const newEngagement = response?.data;
      if (newEngagement?.id) {
        queryClient.setQueryData(['engagement', newEngagement.id], response);
      }
    },
    onError: (error) => {
      console.error('❌ Create failed:', error);
    },
  });
};

// ── Update (optimistic update for BOTH main + won caches) ─────────────
export const useUpdateEngagement = () => {
  const queryClient = useQueryClient();

  return useMutation({
    // payload is already snake_case from formDataToPayload() — pass through directly
    mutationFn: ({ engagementId, updates }) =>
      engagementsApi.updateEngagement(engagementId, updates),

    onMutate: async ({ engagementId, updates }) => {
      // Cancel any in-flight refetches so they don't overwrite our optimistic update
      await queryClient.cancelQueries({ queryKey: ['engagements'] });
      await queryClient.cancelQueries({ queryKey: ['engagements', 'won'] });

      // Snapshot both caches for rollback
      const previousEngagements = queryClient.getQueryData(['engagements']);
      const previousWonEntries  = queryClient
        .getQueriesData({ queryKey: ['engagements', 'won'] })
        .map(([key, data]) => ({ key, data }));

      // 1. Optimistically update main engagements cache
      queryClient.setQueryData(['engagements'], (old) => {
        if (!old?.engagements) return old;
        return {
          ...old,
          engagements: old.engagements.map((eng) =>
            eng.id === engagementId ? { ...eng, ...updates } : eng
          ),
        };
      });

      // 2. Optimistically update ALL won cache entries (one per unique filter combo)
      queryClient
        .getQueriesData({ queryKey: ['engagements', 'won'] })
        .forEach(([key]) => {
          queryClient.setQueryData(key, (old) => {
            if (!old?.engagements) return old;
            return {
              ...old,
              engagements: old.engagements.map((eng) =>
                eng.id === engagementId ? { ...eng, ...updates } : eng
              ),
            };
          });
        });

      return { previousEngagements, previousWonEntries };
    },

    onSuccess: (response, { engagementId }) => {
      const serverData = response?.data || response;
      if (!serverData?.id) return;

      // Replace optimistic data with confirmed server response in main cache
      queryClient.setQueryData(['engagements'], (old) => {
        if (!old?.engagements) return old;
        return {
          ...old,
          engagements: old.engagements.map((eng) =>
            eng.id === engagementId ? { ...eng, ...serverData } : eng
          ),
        };
      });

      // Replace optimistic data in ALL won cache entries
      queryClient
        .getQueriesData({ queryKey: ['engagements', 'won'] })
        .forEach(([key]) => {
          queryClient.setQueryData(key, (old) => {
            if (!old?.engagements) return old;
            return {
              ...old,
              engagements: old.engagements.map((eng) =>
                eng.id === engagementId ? { ...eng, ...serverData } : eng
              ),
            };
          });
        });
    },

    onError: (err, variables, context) => {
      console.error('❌ Update failed:', err);
      // Roll back main cache
      if (context?.previousEngagements) {
        queryClient.setQueryData(['engagements'], context.previousEngagements);
      }
      // Roll back all won cache entries
      context?.previousWonEntries?.forEach(({ key, data }) => {
        queryClient.setQueryData(key, data);
      });
    },
  });
};

// ── Delete ────────────────────────────────────────────────────────────
export const useDeleteEngagement = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (engagementId) => engagementsApi.deleteEngagement(engagementId),
    onSuccess: (_, engagementId) => {
      queryClient.invalidateQueries({ queryKey: ['engagements'], refetchType: 'active' });
      queryClient.invalidateQueries({ queryKey: ['engagements', 'won'] });
      queryClient.removeQueries({ queryKey: ['engagement', engagementId] });
    },
    onError: (error) => {
      console.error('❌ Delete failed:', error);
    },
  });
};

// ── Sync actuals (single) ─────────────────────────────────────────────
export const useSyncActuals = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (engagementId) => engagementsApi.syncActuals(engagementId),
    onSuccess: (_, engagementId) => {
      queryClient.invalidateQueries({ queryKey: ['engagements'] });
      queryClient.invalidateQueries({ queryKey: ['engagements', 'won'] });
      queryClient.invalidateQueries({
        queryKey: ['bid-engagement-components', engagementId],
      });
    },
    onError: (error, engagementId) => {
      console.error('❌ Sync failed:', engagementId, error);
    },
  });
};

// ── Won engagements (bid calculator reference) ────────────────────────
export const useWonEngagements = (filters = {}) => {
  return useQuery({
    queryKey: ['engagements', 'won', filters],
    queryFn: async () => {
      const params = { bid_status: 'Won', limit: filters.limit || 50 };
      if (filters.engagement_type) params.engagement_type = filters.engagement_type;
      if (filters.search || filters.searchClient) params.search = filters.search || filters.searchClient;
      if (filters.industry) params.industry = filters.industry;
      if (filters.time_period || filters.timePeriod) params.time_period = filters.time_period || filters.timePeriod;
      if (filters.start_date || filters.startDate) params.start_date = filters.start_date || filters.startDate;
      if (filters.end_date || filters.endDate) params.end_date = filters.end_date || filters.endDate;
      if (filters.exclude_unknown !== undefined) params.exclude_unknown = filters.exclude_unknown;
      else if (filters.excludeUnknown !== undefined) params.exclude_unknown = filters.excludeUnknown;
      return engagementsApi.getWonEngagements(params);
    },
    enabled: filters.enabled !== false,
    staleTime: 2 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });
};

// ── QB customers dropdown ─────────────────────────────────────────────
export const useQBCustomers = ({ search, limit = 100, enabled = true } = {}) => {
  return useQuery({
    queryKey: ['qb-customers', { search, limit }],
    queryFn: () => qbCustomersApi.listQBCustomers({ search, limit }),
    enabled,
    staleTime: 15 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
  });
};

// ── Sync all actuals (bulk) ───────────────────────────────────────────
export const useSyncAllActuals = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: syncAllEngagementActuals,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['engagements'] });
      queryClient.invalidateQueries({ queryKey: ['engagements', 'won'] });
    },
    onError: (error) => {
      console.error('❌ Bulk sync failed:', error);
    },
  });
};